import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from '../lib/gsapConfig';
import { generateNotification } from '../data/activityData';
import { HeroHighlight, Highlight } from './ui/HeroHighlight';

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const accentColors = {
  quote:     'text-amber-700',
  review:    'text-yellow-700',
  match:     'text-emerald-700',
  completed: 'text-blue-700',
  joined:    'text-purple-700',
  accepted:  'text-green-700',
  live:      'text-green-600',
};

export default function LiveActivity() {
  const sectionRef = useRef(null);
  const tickerRef = useRef(null);
  const [notification, setNotification] = useState(() => generateNotification());
  const [movers, setMovers] = useState(() => rand(10, 24));
  const [quotes, setQuotes] = useState(() => rand(34, 68));

  const nextNotification = useCallback(() => {
    if (!tickerRef.current) return;
    gsap.to(tickerRef.current, {
      y: -10,
      opacity: 0,
      duration: 0.22,
      ease: 'power2.in',
      onComplete: () => {
        setNotification(generateNotification());
        gsap.fromTo(tickerRef.current, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.28, ease: 'power2.out' });
      },
    });
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.activity-word', {
        scrollTrigger: { trigger: sectionRef.current, start: 'top 78%' },
        y: 46, opacity: 0, duration: 0.75, stagger: 0.06, ease: 'power4.out',
      });
      gsap.from('.activity-card', {
        scrollTrigger: { trigger: sectionRef.current, start: 'top 68%' },
        y: 30, opacity: 0, duration: 0.55, stagger: 0.08, ease: 'power3.out',
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const id = setInterval(nextNotification, 4000);
    return () => clearInterval(id);
  }, [nextNotification]);

  useEffect(() => {
    const id = setInterval(() => {
      setMovers((n) => (n >= 30 ? rand(10, 16) : n + rand(1, 2)));
      setQuotes((n) => (n >= 80 ? rand(34, 44) : n + rand(1, 3)));
    }, 40000);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={sectionRef} className="bg-white">
      <HeroHighlight containerClassName="py-14 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <span className="inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary">
                Mover activity
              </span>
              <h2 className="gsap-perspective-parent mt-4 text-3xl font-black tracking-normal text-slate-900 sm:text-5xl">
                {['Real', 'Movement,'].map((word) => (
                  <span key={word} className="hero-clip-overflow mr-2 inline-block pb-1">
                    <span className="activity-word inline-block">{word}</span>
                  </span>
                ))}
                <Highlight className="text-slate-900">
                  <span className="hero-clip-overflow mr-2 inline-block pb-1">
                    <span className="activity-word inline-block">Clear Choice</span>
                  </span>
                </Highlight>
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                See recent quote activity, mover availability, and popular service requests in a simple way.
              </p>
            </div>

            <div className="activity-card rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  { value: movers, label: 'Movers checking schedules' },
                  { value: quotes, label: 'Recent quote requests' },
                  { value: 6, label: 'Service types supported' },
                ].map(({ value, label }) => (
                  <div key={label} className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                    <div className="text-3xl font-black text-primary">{value}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-500">{label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="mb-3 flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
                  </span>
                  <span className="text-sm font-black text-green-700">Live Activity</span>
                </div>

                <div ref={tickerRef} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="min-w-0">
                    <div className={`text-sm font-bold leading-snug ${accentColors[notification.category]}`}>
                      {notification.headline}
                    </div>
                    <div className="mt-1 truncate text-sm text-slate-600">{notification.detail}</div>
                    <div className="mt-1 text-xs text-slate-400">{notification.time}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroHighlight>
    </section>
  );
}
